import {
  defer,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  mergeWith,
  type Observable,
  switchMap,
  timer,
} from 'rxjs'
import { startWith } from 'rxjs/operators'
import { shareLatest } from '@/utils/rx-util'
import { pageActivityStream } from '@/infra/page-activity'

/**
 * Returns a stream of whether a media query is matched.
 * Emits the current value on subscribe.
 * Subscribers will share a single underlying stream.
 * @param query
 */
export function mediaQueryMatchStream(query: string): Observable<boolean> {
  return defer(() => {
    const mq = window.matchMedia(query)
    return fromEvent<MediaQueryListEvent>(mq, 'change').pipe(
      map((e) => e.matches),
      startWith(mq.matches),
      // Re-evaluate the media query any time the page becomes visible/focused.
      // This is to make sure we don't miss any changes.
      mergeWith(
        pageActivityStream().pipe(
          filter((p) => p.visible && p.focused),
          map(() => mq.matches),
        ),
      ),
    )
  }).pipe(distinctUntilChanged(), shareLatest())
}

/**
 * Observable that emits `true` when we're online and `false` when not.
 */
export const isOnline$ = defer(() =>
  merge(
    // React to online status change events.
    fromEvent(window, 'online'),
    fromEvent(window, 'offline'),
  ),
).pipe(
  startWith(null),
  map(() => navigator.onLine),
  distinctUntilChanged(),
)

/**
 * Tracks whether the user is active.
 */
export const userActive$ = defer(() =>
  merge(
    fromEvent(window, 'mousemove'),
    fromEvent(window, 'wheel'),
    fromEvent(window, 'keydown'),
    fromEvent(window, 'touchstart'),
  ),
).pipe(
  startWith(null),
  switchMap(() =>
    timer(2 * 60_000).pipe(
      map(() => false),
      startWith(true),
    ),
  ),
  distinctUntilChanged(),
  shareLatest(),
)

/**
 * Checks whether the {@link child} is an ancestor of {@link ancestor}.
 *
 * @param ancestor
 * @param child
 */
export function isAncestorElement(ancestor: Element, child: Element): boolean {
  let candidate: Element | null = child.parentElement ?? null
  do {
    if (candidate === ancestor) {
      return true
    }
  } while ((candidate = candidate?.parentElement ?? null))

  return false
}

/**
 * Gets the scroll-left position needed to center the {@link child} in {@link scrollingParent}.
 *
 * @param scrollingParent
 * @param child
 */
export function getScrollLeftForCentering(
  scrollingParent: Element,
  child: Element,
) {
  const parentRect = scrollingParent.getBoundingClientRect()
  const childRect = child.getBoundingClientRect()

  // Calculate the left offset relative to the scroll container,
  // taking into account the current scroll.
  const subjectRelativeLeft =
    childRect.left - parentRect.left + scrollingParent.scrollLeft
  return (
    subjectRelativeLeft - (scrollingParent.clientWidth - childRect.width) / 2
  )
}

/**
 * Gets the scroll-top position needed to center the {@link child} in {@link scrollingParent}.
 *
 * @param scrollingParent
 * @param child
 */
export function getScrollTopForCentering(
  scrollingParent: Element,
  child: Element,
) {
  const parentRect = scrollingParent.getBoundingClientRect()
  const childRect = child.getBoundingClientRect()

  // Calculate the left offset relative to the scroll container,
  // taking into account the current scroll.
  const subjectRelativeTop =
    childRect.top - parentRect.top + scrollingParent.scrollTop
  return (
    subjectRelativeTop - (scrollingParent.clientHeight - childRect.height) / 2
  )
}

/**
 * Checks for Mobile Safari.
 */
export function isMobileSafari() {
  const ua = navigator.userAgent
  const isSafari = ua.includes('Safari') && !ua.includes('Chrome')
  const isMobile = ua.includes('iPhone') || ua.includes('iPad')
  return isSafari && isMobile
}

/**
 * Checks for Safari (desktop or mobile).
 */
export function isSafari() {
  const ua = navigator.userAgent
  return ua.includes('Safari') && !ua.includes('Chrome')
}

/**
 * Normalizes the value to be a valid CSS number.
 *
 * @param value
 */
export function normalizeCssNumber(value: string | number | null) {
  return value === null
    ? null
    : typeof value === 'number'
      ? `${value}px`
      : value
}
